/**
 * Derives the customer-app page background from the client's primary color.
 * The color is darkened (RGB channels scaled proportionally, preserving hue)
 * until its WCAG relative luminance reaches `target`, so the white modal card
 * always floats cleanly on top. Pure, Angular-free (matches branding-contrast).
 */
import { hexToRgbTuple, luminanceForContrastOn, relativeLuminance, relativeLuminanceRgb } from './branding-contrast';

const FALLBACK = '#1a1a1a';

/** How far a hover state moves from its resting fill (fraction toward black, or toward white when already dark). */
const HOVER_SHIFT = 0.15;

/** Below this luminance, darkening is imperceptible, so hover lightens instead. */
const DARK_FILL_LUMINANCE = 0.1;

const toHexChannel = (value: number): string =>
  Math.max(0, Math.min(255, Math.round(value)))
    .toString(16)
    .padStart(2, '0');

const rgbToHex = ([r, g, b]: [number, number, number]): string => `#${toHexChannel(r)}${toHexChannel(g)}${toHexChannel(b)}`;

/**
 * Returns `hex` darkened to ~`target` relative luminance as `#rrggbb`.
 * Never lightens (inputs already at/below target are returned unchanged).
 * Invalid/unparseable hex → '#1a1a1a'.
 */
// Recursion used instead of loop due to `no-loops` ESLint rule.
// Evaluates the UNROUNDED scaled tuple: luminance is monotonic in k only before rounding, so
// measuring `rgbToHex(scaled)` here would search a step function and lose precision (it also
// made this implementation disagree with linxapp's by a channel step).
const binarySearchK = (rgb: [number, number, number], target: number, lo: number, hi: number, iterations: number): number => {
  if (iterations === 0) {
    return (lo + hi) / 2;
  }
  const k = (lo + hi) / 2;
  if (relativeLuminanceRgb([rgb[0] * k, rgb[1] * k, rgb[2] * k]) > target) {
    return binarySearchK(rgb, target, lo, k, iterations - 1);
  }
  return binarySearchK(rgb, target, k, hi, iterations - 1);
};

const brightest = (candidates: string[]): string =>
  candidates.reduce((best, candidate) => (relativeLuminance(candidate) > relativeLuminance(best) ? candidate : best));

const darkest = (candidates: string[]): string =>
  candidates.reduce((best, candidate) => (relativeLuminance(candidate) < relativeLuminance(best) ? candidate : best));

/**
 * Brightest candidate that does NOT exceed `target`, or the darkest one when none qualify.
 *
 * Staying at or below `target` is a hard requirement, not a preference: `darkenForContrastOn`
 * converts a contrast floor into a luminance target, so overshooting by even one channel step
 * fails the ratio (KOR-14961 — the previous implementation seeded its search with the rounded
 * candidate without testing it against `target`, so a round-up could be returned even when a
 * qualifying darker candidate existed).
 */
const pickWithinTarget = (candidates: string[], target: number): string => {
  const withinTarget = candidates.filter((candidate) => relativeLuminance(candidate) <= target);
  return withinTarget.length > 0 ? brightest(withinTarget) : darkest(candidates);
};

export const darkenToLuminance = (hex: string, target = 0.05): string => {
  const rgb = hexToRgbTuple(hex);
  if (!rgb) {
    return FALLBACK;
  }
  if (relativeLuminance(hex) <= target) {
    return rgbToHex(rgb);
  }
  // Binary-search a uniform scale factor k in [0,1]; luminance is monotonic in k.
  const k = binarySearchK(rgb, target, 0, 1, 50);
  const baseScaled: [number, number, number] = [rgb[0] * k, rgb[1] * k, rgb[2] * k];
  const baseHex = rgbToHex(baseScaled);

  // Try with floor/ceil rounding to account for rounding effects
  const candidates = [
    baseHex,
    rgbToHex([Math.floor(baseScaled[0]), Math.floor(baseScaled[1]), Math.floor(baseScaled[2])]),
    rgbToHex([Math.ceil(baseScaled[0]), Math.ceil(baseScaled[1]), Math.ceil(baseScaled[2])]),
  ];

  return pickWithinTarget(candidates, target);
};

/**
 * Darkens `hex` only as far as clearing `minRatio` against `bg` requires, preserving hue.
 * A brand that already passes is returned unchanged, so this is a no-op for every colour
 * that was rendering correctly (KOR-14961: a light primary made the CTA fill invisible on
 * the white card, while darkening it outright would have altered every brand).
 * Invalid `hex` → '#1a1a1a'. An unparseable `bg` leaves a valid colour alone.
 */
export const darkenForContrastOn = (hex: string, bg = '#ffffff', minRatio = 1.5): string => {
  const target = luminanceForContrastOn(bg, minRatio);
  if (Number.isNaN(target)) {
    return hexToRgbTuple(hex) ? hex : FALLBACK;
  }
  return darkenToLuminance(hex, target);
};

/**
 * Hover variant of a resolved button fill. Moves `HOVER_SHIFT` toward black, which
 * reproduces the hand-picked Cateno token pair (#ff7403 → #d96303) exactly, so a derived
 * hover matches what design chose by hand. Fills too dark to darken perceptibly move
 * toward white instead. Invalid hex is returned unchanged so the caller's fallback governs.
 */
export const hoverShade = (hex: string): string => {
  const rgb = hexToRgbTuple(hex);
  if (!rgb) {
    return hex;
  }
  const [r, g, b] = rgb;
  if (relativeLuminance(hex) < DARK_FILL_LUMINANCE) {
    return rgbToHex([r + (255 - r) * HOVER_SHIFT, g + (255 - g) * HOVER_SHIFT, b + (255 - b) * HOVER_SHIFT]);
  }
  return rgbToHex([r * (1 - HOVER_SHIFT), g * (1 - HOVER_SHIFT), b * (1 - HOVER_SHIFT)]);
};
